import { FormEvent, useState } from 'react';
import { api, Session, setToken, User } from '../lib/api';
import crestLogo from '../assets/brand/favicon.ico';

export function Login({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{
        token: string;
        user: User;
        displacedSessions: Array<{ ip: string | null; device: string | null }>;
      }>('/api/auth/login', { email, password });
      setToken(result.token);
      // One session per account: say so rather than silently ending the other device's session.
      // Someone whose password has been stolen should see that this happened.
      if (result.displacedSessions?.length) {
        const other = result.displacedSessions[0];
        sessionStorage.setItem(
          'ssemr.mediator.displaced',
          `Signed out your other session on ${other?.device ?? 'another device'}` +
            (other?.ip ? ` (${other.ip})` : ''),
        );
      }
      onSignedIn(await api.get<Session>('/api/auth/me'));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <img src={crestLogo} alt="SSEMR" className="login-logo" />
          <span className="login-eyebrow">SSEMR</span>
          <h1 className="login-title">Interoperability Hub</h1>
          <p className="login-subtitle">Aggregate reporting to DHIS2</p>
        </div>

        {error ? <div className="banner banner-danger">{error}</div> : null}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button className="btn btn-primary login-submit" disabled={busy}>
          {busy ? <span className="spinner" /> : null}
          {busy ? 'Signing in' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
